import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import { sendBirdSelectors } from '@sendbird/uikit-react';
import { useSelector, useDispatch } from 'react-redux';
import { type AnyObject } from '@Types';
import { type User } from '@sendbird/chat';
import { promiseCacheFactory } from 'PromiseCacheFactory';
import { useCallback } from 'react';
import { HiddenChannelFilter, type GroupChannelListQuery } from '@sendbird/chat/groupChannel';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { getUserChannelMap, getUsersFromUserChannelMap } from '../selectors';
import { setUserChannelMap } from '../actions';

const [promiseCache] = promiseCacheFactory();

const generateCacheKey = (userId: string) => `${userId}:useGetUserChannelMap_promise_key`;

// channelMap is an object where the key is a stringified array of OTHER users in a channel
// and the value is that channels URL we will build a record for each channel a user has
type ChannelMap = AnyObject<string>;

const appendToChannelMap = (
    userId: string,
    channelMap: ChannelMap,
    channels: Awaited<ReturnType<GroupChannelListQuery['next']>>,
) => {
    const updatedChannelMap = channels.reduce(
        (prev, curr) => ({
            ...prev,
            [JSON.stringify(
                curr.members
                    .filter(m => m.userId !== userId)
                    .map(m => m.userId)
                    .sort(),
            )]: curr.url,
        }),
        channelMap,
    );
    const users = channels.flatMap(c => c.members.filter(m => m.userId !== userId));

    return [updatedChannelMap, users] as const;
};

const recursivelyFetchChannels = ({
    userId,
    channelQuery,
    onComplete,
}: {
    userId: string;
    channelQuery: GroupChannelListQuery;
    onComplete: (channelMap: ChannelMap) => void;
}) => {
    const fetchAllChannels = (q: GroupChannelListQuery, u: User[] = [], channelMap: ChannelMap = {}) => {
        if (q.hasNext) {
            q.next()
                .then(channels => {
                    const [updatedChannelMap, users] = appendToChannelMap(userId, channelMap, channels);
                    setTimeout(() => {
                        fetchAllChannels(q, [...users, ...u], updatedChannelMap);
                    }, 75);
                })
                .catch(e => {
                    // catch errors that are do to requests being cancelled
                    if (e?.message && e.message.match?.('Request has been canceled.')) return;

                    throw e;
                });
        } else {
            onComplete(channelMap);
        }
    };
    fetchAllChannels(channelQuery);
};

const useCreateChannelMapPromise = (userId: string) => {
    const sbContext = useSendbirdStateContext();
    const sdk = sendBirdSelectors.getSdk(sbContext);
    const dispatch = useDispatch();

    const createSetUserChannelMapPromise = useCallback(
        ({ dispatchToRedux = true } = {}) => {
            const channelQuery = sdk.groupChannel.createMyGroupChannelListQuery({
                hiddenChannelFilter: HiddenChannelFilter.ALL,
            });

            return new Promise(r => {
                recursivelyFetchChannels({
                    userId,
                    channelQuery,
                    onComplete: channelMap => {
                        if (dispatchToRedux) {
                            dispatch(setUserChannelMap(channelMap || {}));
                        }
                        r(channelMap);
                    },
                });
            });
        },
        [sdk, dispatch, userId],
    );

    return createSetUserChannelMapPromise;
};

const CHANNEL_MAP_CACHE_KEY = 'messaging-channel-map';

function getCachedChannelMap(userId: string): ChannelMap | null {
    try {
        const raw = sessionStorage.getItem(`${CHANNEL_MAP_CACHE_KEY}:${userId}`);
        if (!raw) return null;
        return JSON.parse(raw) as ChannelMap;
    } catch {
        return null;
    }
}

function setCachedChannelMap(userId: string, map: ChannelMap) {
    try {
        sessionStorage.setItem(`${CHANNEL_MAP_CACHE_KEY}:${userId}`, JSON.stringify(map));
    } catch {
        // sessionStorage full or unavailable — ignore
    }
}

export const useGetUserChannelMap = () => {
    const { id } = useRequiredCurrentUser();
    const userChannelMap = useSelector(getUserChannelMap);
    const usersFromChannelMap = useSelector(state => getUsersFromUserChannelMap(state, id));
    const dispatch = useDispatch();
    const createSetUserChannelMapPromise = useCreateChannelMapPromise(id);

    if (!userChannelMap) {
        // Try sessionStorage cache first for instant display
        const cached = getCachedChannelMap(id);
        if (cached) {
            dispatch(setUserChannelMap(cached));
            // Refresh sessionStorage in the background but don't re-dispatch to Redux.
            // Re-dispatching would change usersWithExistingMessages → relevantUserIds →
            // trigger a new useSuspenseQuery that re-suspends the ChannelBuilder.
            createSetUserChannelMapPromise({ dispatchToRedux: false }).then(map => {
                if (map) setCachedChannelMap(id, map as ChannelMap);
            });
        } else {
            // No cache — suspend while fetching, then cache the result
            const promise = createSetUserChannelMapPromise().then(map => {
                if (map) setCachedChannelMap(id, map as ChannelMap);
                return map;
            });
            throw promiseCache(generateCacheKey(id), promise);
        }
    }

    return [usersFromChannelMap, userChannelMap] as const;
};
